import { useCallback, useState } from 'react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import type { AllChatterTableType } from '@/modules/tracking/chatter-tracking/service/chatter-tracking-service.ts'
import { classNames } from '@/utils/helpers'

interface Props {
  chatters: AllChatterTableType[]
  select: AllChatterTableType[]
  onAllSelect: () => void
  onAllRemove: () => void
  onAddSelect: (chatter: AllChatterTableType) => void
  onRemoveSelect: (chatter: AllChatterTableType) => void
}

export const ChatterCombobox = ({
  chatters,
  select,
  onAllSelect,
  onAllRemove,
  onAddSelect,
  onRemoveSelect,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [isAllSelected, setAllSelected] = useState(true)

  const handleAllCreatorSelect = useCallback(() => {
    if (isAllSelected) {
      onAllRemove()
    } else {
      onAllSelect()
    }

    setAllSelected(!isAllSelected)
  }, [isAllSelected, onAllRemove, onAllSelect])

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex justify-between"
        >
          <div className="flex gap-2 font-normal">
            {select.length === chatters.length ? (
              <span>All Chatters</span>
            ) : select.length > 1 ? (
              <span> {select.length} Chatters selected</span>
            ) : select.length === 1 ? (
              <span>{chatters.filter((c) => select.includes(c))[0]?.name}</span>
            ) : (
              <span>Select Chatters</span>
            )}
          </div>
          <ExpandMoreIcon className="w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandList>
            <CommandInput placeholder="Search Chatter" />
            <CommandEmpty>No Chatters found.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={handleAllCreatorSelect}>
                <div
                  className={classNames(
                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-500',
                    isAllSelected ? 'bg-primary-500 text-white' : 'opacity-50',
                  )}
                >
                  {isAllSelected && <CheckIcon className="w-3" />}
                </div>
                All Chatters
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup className=" max-h-44 overflow-auto">
              {chatters.map((chatter) => {
                const isSelected = select.includes(chatter)

                return (
                  <CommandItem
                    key={chatter.name}
                    onSelect={() => {
                      if (isSelected) {
                        onRemoveSelect(chatter)
                        setAllSelected(false)
                      } else {
                        onAddSelect(chatter)
                        if (select.length === chatters.length - 1) {
                          setAllSelected(true)
                        }
                      }
                    }}
                  >
                    <div
                      className={classNames(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary-500',
                        isSelected ? 'bg-primary-500 text-white' : 'opacity-50',
                      )}
                    >
                      {isSelected && <CheckIcon className="w-3" />}
                    </div>
                    {chatter.name}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
